import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useVendoresBC } from '@/hooks/useVendoresBC';
import { TERMINO_PAGO_CONTADO } from '@adelante/shared';
import { CaretDown, LinkBreak, MagnifyingGlass } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';

interface Props {
  /** vendorCode seleccionado, o null si no está vinculado a ningún proveedor de BC. */
  value: string | null;
  onChange: (vendorCode: string | null) => void;
  className?: string;
  /** Texto cuando no hay proveedor elegido. Por defecto el de la vinculación opcional de Flujo Semanal. */
  placeholder?: string;
  /**
   * Mostrar la lista dentro del flujo de la página en vez de en una ventana flotante. Es lo que hay que
   * usar dentro de un Dialog: el popover flotante y el Dialog tienen copias distintas de la librería de
   * foco de Radix y se pelean, así que el foco vuelve al botón en cada tecla y no se puede escribir.
   */
  enLinea?: boolean;
  /** Ordena primero a los proveedores con documentos pendientes y muestra cuántos (vendorCode → cantidad). */
  documentosPorProveedor?: Record<string, number>;
  /** Cómo se llama el documento en la insignia ("OC", "fact."…). */
  etiquetaDocumentos?: string;
  /** Muestra solo a los proveedores que tienen documentos pendientes. */
  soloConDocumentos?: boolean;
  /** Ofrece el filtro de contado (términos CONTADO o sin términos; excluye los de crédito), activado de entrada. */
  filtroContado?: boolean;
}

/**
 * Selector de un único proveedor de Business Central (pagos.Vendor), para
 * vincularlo a un proveedor manual de Flujo Semanal. A diferencia de
 * `<MultiSelect>` es de selección única y cierra el popover al elegir.
 */
export function VendorPickerBC({
  value,
  onChange,
  className,
  placeholder,
  enLinea,
  documentosPorProveedor,
  etiquetaDocumentos = 'OC',
  soloConDocumentos,
  filtroContado,
}: Props) {
  const { data, isLoading } = useVendoresBC();
  const [busqueda, setBusqueda] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [soloContado, setSoloContado] = useState(true);

  const vendors = data?.data ?? [];
  const seleccionado = vendors.find((v) => v.vendorCode === value);

  const aplicaContado = !!filtroContado && soloContado;
  const filtrados = useMemo(() => {
    const norm = busqueda.trim().toLowerCase();
    const lista = vendors.filter(
      (v) =>
        // Contado, o sin términos cargados en BC (muchos pagos de contado están así). Quedan fuera los de crédito.
        (!soloConDocumentos || (documentosPorProveedor?.[v.vendorCode] ?? 0) > 0) &&
        (!aplicaContado || !v.paymentTermsCode || v.paymentTermsCode === TERMINO_PAGO_CONTADO) &&
        (!norm ||
          (v.vendorName ?? '').toLowerCase().includes(norm) ||
          v.vendorCode.toLowerCase().includes(norm)),
    );
    if (!documentosPorProveedor) return lista;
    // Primero los que tienen OC vigentes (los de más OC arriba); dentro de cada grupo, por nombre.
    return [...lista].sort(
      (a, b) =>
        (documentosPorProveedor[b.vendorCode] ?? 0) - (documentosPorProveedor[a.vendorCode] ?? 0) ||
        (a.vendorName ?? '').localeCompare(b.vendorName ?? '', 'es'),
    );
  }, [vendors, busqueda, aplicaContado, documentosPorProveedor, soloConDocumentos]);

  if (seleccionado) {
    return (
      <div className={className ?? 'flex items-center gap-2'}>
        <div className="flex items-center gap-2 rounded-md border border-adelante-primary/40 bg-adelante-primary/10 px-2.5 py-1.5 text-sm">
          <span className="font-medium">{seleccionado.vendorName}</span>
          <span className="text-xs text-muted-foreground">({seleccionado.vendorCode})</span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground"
          title="Desvincular de BC"
          onClick={() => onChange(null)}
        >
          <LinkBreak className="h-4 w-4" weight="bold" aria-hidden="true" />
        </Button>
      </div>
    );
  }

  const elegir = (codigo: string) => {
    onChange(codigo);
    setBusqueda('');
    setAbierto(false);
  };

  const buscador = (
    <>
      <div className="relative mb-2">
        <MagnifyingGlass
          className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
          weight="bold"
          aria-hidden="true"
        />
        <Input
          placeholder="Buscar proveedor..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="pl-8"
          autoFocus={enLinea}
        />
      </div>
      {filtroContado && (
        <label className="mb-2 flex items-center gap-2 px-1 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={soloContado}
            onChange={(e) => setSoloContado(e.target.checked)}
          />
          Contado o sin términos de pago (sin crédito)
          <span className="ml-auto">{filtrados.length}</span>
        </label>
      )}
      <div className="max-h-64 space-y-0.5 overflow-auto">
        {filtrados.length === 0 ? (
          <p className="px-2 py-1.5 text-sm text-muted-foreground">
            Sin resultados
            {aplicaContado && ' con ese filtro: desmarcalo para ver todos'}
          </p>
        ) : (
          filtrados.map((v) => (
            <button
              key={v.vendorCode}
              type="button"
              onClick={() => elegir(v.vendorCode)}
              className="flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
            >
              <span className="flex min-w-0 flex-col items-start">
                <span className="max-w-full truncate font-medium">{v.vendorName}</span>
                <span className="text-xs text-muted-foreground">{v.vendorCode}</span>
              </span>
              {(documentosPorProveedor?.[v.vendorCode] ?? 0) > 0 && (
                <span
                  className="shrink-0 rounded-full bg-adelante-primary/20 px-2 py-0.5 text-xs font-medium"
                  title="Documentos pendientes en Business Central"
                >
                  {documentosPorProveedor?.[v.vendorCode]} {etiquetaDocumentos}
                </span>
              )}
            </button>
          ))
        )}
      </div>
    </>
  );

  const boton = (
    <Button
      type="button"
      variant="outline"
      className={className ?? 'w-full justify-between font-normal'}
      onClick={enLinea ? () => setAbierto((v) => !v) : undefined}
    >
      <span className="text-muted-foreground">
        {isLoading
          ? 'Cargando proveedores BC...'
          : (placeholder ?? 'Vincular con proveedor de BC (opcional)')}
      </span>
      <CaretDown className="h-4 w-4 shrink-0 opacity-50" weight="bold" aria-hidden="true" />
    </Button>
  );

  if (enLinea) {
    return (
      <div className="space-y-1">
        {boton}
        {abierto && <div className="rounded-md border bg-popover p-2">{buscador}</div>}
      </div>
    );
  }

  return (
    // modal: sin esto, cuando este Popover vive dentro de un Dialog (ej. "Nuevo
    // proveedor"), el FocusScope del Dialog le roba el foco al input de búsqueda en
    // cada tecla — Radix solo coopera correctamente entre focus-scopes anidados si
    // ambos son "modal" (se registran en la misma pila en vez de pelear por el foco).
    <Popover open={abierto} onOpenChange={setAbierto} modal>
      <PopoverTrigger asChild>{boton}</PopoverTrigger>
      <PopoverContent className="w-[320px] p-2" align="start">
        {buscador}
      </PopoverContent>
    </Popover>
  );
}
